import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { LayoutGrid, Check, X, Filter, AlertTriangle, Loader2 } from "lucide-react";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AU_STATES } from "@/lib/api";

const STATUS_LABEL = {
  displayed: "Displayed",
  not_displayed: "Not displayed",
  unchecked: "Not yet checked",
};

const chip = (status) => {
  if (status === "displayed") {
    return (
      <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
        <Check className="w-3.5 h-3.5" strokeWidth={3} /> Displayed
      </span>
    );
  }
  if (status === "not_displayed") {
    return (
      <span className="inline-flex items-center gap-1 text-red-700 font-semibold">
        <X className="w-3.5 h-3.5" strokeWidth={3} /> Not displayed
      </span>
    );
  }
  return <span className="text-slate-400">— Unchecked</span>;
};

function FloorDisplayReportInner() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [history, setHistory] = useState([]);
  const [categories, setCategories] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    category_id: "",
    model_id: "",
    state: "",
    status: "",
  });

  // Load filter options once
  useEffect(() => {
    api
      .get("/product-categories")
      .then((r) => setCategories(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCategories([]));
  }, []);

  // Models list follows the selected category
  useEffect(() => {
    const params = { active_only: true };
    if (filters.category_id) params.category_id = filters.category_id;
    api
      .get("/products", { params })
      .then((r) => setModels(Array.isArray(r.data) ? r.data : []))
      .catch(() => setModels([]));
  }, [filters.category_id]);

  // Whenever a filter changes, refetch coverage + history
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params[k] = v;
    });

    Promise.all([
      api.get("/product-coverage", { params }),
      api.get("/floor-display/history", { params: { limit: 100 } }),
    ])
      .then(([cov, hist]) => {
        if (cancelled) return;
        // Backend returns { summary: [...], rows: [...] }
        const payload = cov?.data && typeof cov.data === "object" ? cov.data : {};
        setSummary(Array.isArray(payload.summary) ? payload.summary : []);
        setRows(Array.isArray(payload.rows) ? payload.rows : []);
        setHistory(Array.isArray(hist?.data) ? hist.data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setSummary([]);
        setRows([]);
        setHistory([]);
        const detail =
          err?.response?.data?.detail ||
          err?.response?.statusText ||
          err?.message ||
          "Failed to load Floor Display Report";
        setError(String(detail));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filters]);

  const counts = useMemo(() => {
    const displayed = rows.filter((r) => r?.status === "displayed").length;
    const notDisplayed = rows.filter((r) => r?.status === "not_displayed").length;
    const unchecked = rows.filter((r) => r?.status === "unchecked").length;
    return { displayed, notDisplayed, unchecked };
  }, [rows]);

  // Filter models by selected category (client-side safety net)
  const modelOptions = useMemo(() => {
    if (!filters.category_id) return models;
    return models.filter((m) => m.category_id === filters.category_id);
  }, [models, filters.category_id]);

  const clearFilters = () =>
    setFilters({ category_id: "", model_id: "", state: "", status: "" });

  return (
    <div className="space-y-6" data-testid="floor-display-report-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight flex items-center gap-2">
          <LayoutGrid className="w-8 h-8 text-[#003DA5]" /> Floor Display Report
        </h1>
        <p className="text-slate-600 mt-1">
          Verified floor-display statuses across all stores + full change history
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div
          className="bg-white rounded-xl border border-slate-200 p-5"
          data-testid="stat-displayed"
        >
          <div className="text-xs uppercase font-semibold text-slate-500">Displayed</div>
          <div className="text-3xl font-heading font-black text-emerald-600 mt-1">
            {counts.displayed}
          </div>
        </div>
        <div
          className="bg-white rounded-xl border border-slate-200 p-5"
          data-testid="stat-not-displayed"
        >
          <div className="text-xs uppercase font-semibold text-slate-500">Not displayed</div>
          <div className="text-3xl font-heading font-black text-red-600 mt-1">
            {counts.notDisplayed}
          </div>
        </div>
        <div
          className="bg-white rounded-xl border border-slate-200 p-5"
          data-testid="stat-unchecked"
        >
          <div className="text-xs uppercase font-semibold text-slate-500">Not yet checked</div>
          <div className="text-3xl font-heading font-black text-amber-600 mt-1">
            {counts.unchecked}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-2 items-center">
        <Filter className="w-4 h-4 text-slate-400" />
        <select
          value={filters.category_id}
          onChange={(e) =>
            setFilters((f) => ({ ...f, category_id: e.target.value, model_id: "" }))
          }
          className="h-10 px-3 border rounded-lg text-sm"
          data-testid="fdr-filter-category"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={filters.model_id}
          onChange={(e) => setFilters((f) => ({ ...f, model_id: e.target.value }))}
          className="h-10 px-3 border rounded-lg text-sm"
          data-testid="fdr-filter-model"
        >
          <option value="">All models</option>
          {modelOptions.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
        <select
          value={filters.state}
          onChange={(e) => setFilters((f) => ({ ...f, state: e.target.value }))}
          className="h-10 px-3 border rounded-lg text-sm"
          data-testid="fdr-filter-state"
        >
          <option value="">All states</option>
          {AU_STATES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          className="h-10 px-3 border rounded-lg text-sm"
          data-testid="fdr-filter-status"
        >
          <option value="">Any status</option>
          <option value="displayed">Displayed</option>
          <option value="not_displayed">Not displayed</option>
          <option value="unchecked">Not yet checked</option>
        </select>
        {(filters.category_id || filters.model_id || filters.state || filters.status) && (
          <button
            onClick={clearFilters}
            className="ml-auto text-xs text-[#003DA5] hover:underline"
            data-testid="fdr-clear-filters"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div
          className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex items-start gap-2"
          data-testid="fdr-error"
        >
          <AlertTriangle className="w-5 h-5 mt-0.5" />
          <div>
            <div className="font-semibold">Could not load the report</div>
            <div className="text-sm">{error}</div>
          </div>
        </div>
      )}

      {/* Coverage table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b font-heading font-semibold text-[#001F54] flex items-center justify-between">
          <span>Current Status ({rows.length} entries)</span>
          {loading && (
            <span className="inline-flex items-center gap-1 text-xs text-slate-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
            </span>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F3F7FC] text-xs uppercase text-slate-500 sticky top-0">
              <tr>
                <th className="text-left px-4 py-3">Store</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-left px-4 py-3">Model</th>
                <th className="text-left px-4 py-3">State</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Units</th>
                <th className="text-left px-4 py-3">Sizes</th>
                <th className="text-left px-4 py-3">Last verified</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={`${r?.customer_id}-${r?.model_id}-${i}`}
                  className="border-t hover:bg-slate-50"
                  data-testid={`fdr-row-${i}`}
                >
                  <td className="px-4 py-2">
                    {r?.customer_id ? (
                      <Link
                        to={`/accounts/${r.customer_id}`}
                        className="text-[#003DA5] hover:underline"
                      >
                        {r.customer_name || "—"}
                      </Link>
                    ) : (
                      r?.customer_name || "—"
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs">{r?.category_name || "—"}</td>
                  <td className="px-4 py-2">{r?.model_name || "—"}</td>
                  <td className="px-4 py-2 text-xs">{r?.state || "—"}</td>
                  <td className="px-4 py-2">{chip(r?.status)}</td>
                  <td className="px-4 py-2">{r?.unit_count ?? 0}</td>
                  <td className="px-4 py-2 text-xs text-slate-600">
                    {Array.isArray(r?.sizes_present) && r.sizes_present.length
                      ? r.sizes_present.join(", ")
                      : "—"}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">
                    {r?.last_verified_at
                      ? new Date(r.last_verified_at).toLocaleString()
                      : "—"}
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan="8"
                    className="px-4 py-12 text-center text-slate-500"
                    data-testid="fdr-empty-rows"
                  >
                    No floor-display records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* History */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b font-heading font-semibold text-[#001F54]">
          Recent Change History
        </div>
        <div className="max-h-80 overflow-y-auto divide-y">
          {history.map((h) => {
            const prevStatus = h?.previous?.status;
            const newStatus = h?.new?.status;
            const isStatusChange = h?.event === "status_changed";
            return (
              <div
                key={h?.id}
                className="p-3 text-sm flex items-center justify-between gap-4"
                data-testid={`fdr-hist-${h?.id}`}
              >
                <div className="min-w-0">
                  <span className="font-medium">{h?.model_name || "—"}</span>{" "}
                  at{" "}
                  {h?.customer_id ? (
                    <Link
                      to={`/accounts/${h.customer_id}`}
                      className="text-[#003DA5] hover:underline"
                    >
                      {h?.customer_name || "—"}
                    </Link>
                  ) : (
                    h?.customer_name || "—"
                  )}
                  {isStatusChange ? (
                    <>
                      {" "}
                      : {chip(prevStatus)} → {chip(newStatus)}
                    </>
                  ) : (
                    <>
                      {" "}
                      · <span className="text-slate-500">{h?.event}</span>
                    </>
                  )}
                </div>
                <div className="text-xs text-slate-500 whitespace-nowrap">
                  {h?.changed_by_name || "—"} ·{" "}
                  {h?.changed_at ? new Date(h.changed_at).toLocaleString() : "—"}
                </div>
              </div>
            );
          })}
          {!loading && history.length === 0 && (
            <div
              className="p-6 text-center text-slate-500"
              data-testid="fdr-empty-history"
            >
              No changes logged yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function FloorDisplayReport() {
  return (
    <ErrorBoundary name="FloorDisplayReport">
      <FloorDisplayReportInner />
    </ErrorBoundary>
  );
}
